import React, { useState } from 'react';
import { X } from 'lucide-react';
import './AuthModal.css';

function AuthModal({ isOpen, onClose }) {
  const [modalMode, setModalMode] = useState('login'); // 'login', 'signup', 'forgot', 'reset'
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [signupForm, setSignupForm] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Handle login form input changes
  const handleLoginChange = (e) => {
    setLoginForm({ ...loginForm, [e.target.id]: e.target.value });
  };

  // Handle signup form input changes
  const handleSignupChange = (e) => {
    setSignupForm({ ...signupForm, [e.target.id]: e.target.value });
  };

  const switchToSignup = (e) => {
    e.preventDefault();
    setModalMode('signup');
    setFormError('');
    setFormSuccess('');
  };

  const switchToLogin = (e) => {
    e.preventDefault();
    setModalMode('login');
    setFormError('');
    setFormSuccess('');
  };

  const switchToForgot = (e) => {
    e.preventDefault();
    setModalMode('forgot');
    setFormError('');
    setFormSuccess('');
  };

  // Handle login form submission
  const handleLogin = async (e) => {
    e.preventDefault();
    setFormError('');
    setIsLoading(true);
    
    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: loginForm.username,
          password: loginForm.password
        }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Login failed');
      }
      
      // Save token to localStorage
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      
      // Close modal
      onClose(true, data.user);
      
    } catch (error) {
      console.error('Login error:', error);
      setFormError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle signup form submission
  const handleSignup = async (e) => {
    e.preventDefault();
    setFormError('');
    
    // Validate passwords match
    if (signupForm.password !== signupForm.confirmPassword) {
      setFormError('Passwords do not match');
      return;
    }
    
    setIsLoading(true);
    
    try {
      const response = await fetch('/api/users/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: signupForm.username,
          email: signupForm.email,
          password: signupForm.password
        }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Registration failed');
      }
      
      // Save token and user to localStorage
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      
      // Close modal with success
      onClose(true, data.user);
      
    } catch (error) {
      console.error('Signup error:', error);
      setFormError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <button className="modal-close-button" onClick={() => onClose(false)}>
          <X size={20} />
        </button>
        {formError && (
          <div className="form-error">
            {formError}
          </div>
        )}
        {formSuccess && (
          <div className="form-success">
            {formSuccess}
          </div>
        )}

        {modalMode === 'login' && (
          <form className="auth-form" onSubmit={handleLogin}>
            <div className="form-group">
              <label htmlFor="username">username</label>
              <input
                type="text"
                id="username"
                placeholder="enter username"
                value={loginForm.username}
                onChange={handleLoginChange}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="password">password</label>
              <input
                type="password"
                id="password"
                placeholder="enter password"
                value={loginForm.password}
                onChange={handleLoginChange}
                required
              />
            </div>
            <div className="form-actions">
              <button type="submit" className="submit-button" disabled={isLoading}>
                {isLoading ? 'logging in...' : 'login'}
              </button>
            </div>
            <div className="form-switch">
              <a href="#" onClick={switchToForgot}>forgot password?</a>
              {' | '}
              <a href="#" onClick={switchToSignup}>sign up</a>
            </div>
          </form>
        )}

        {modalMode === 'signup' && (
          <form className="auth-form" onSubmit={handleSignup}>
            <div className="form-group">
              <label htmlFor="username">username</label>
              <input
                type="text"
                id="username"
                placeholder="enter username"
                value={signupForm.username}
                onChange={handleSignupChange}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="email">email</label>
              <input
                type="email"
                id="email"
                placeholder="enter email"
                value={signupForm.email}
                onChange={handleSignupChange}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="password">password</label>
              <input
                type="password"
                id="password"
                placeholder="enter password"
                value={signupForm.password}
                onChange={handleSignupChange}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="confirmPassword">confirm password</label>
              <input
                type="password"
                id="confirmPassword"
                placeholder="confirm password"
                value={signupForm.confirmPassword}
                onChange={handleSignupChange}
                required
              />
            </div>
            <div className="form-actions">
              <button type="submit" className="submit-button" disabled={isLoading}>
                {isLoading ? 'signing up...' : 'sign up'}
              </button>
            </div>
            <div className="form-switch">
              <a href="#" onClick={switchToLogin}>back to login</a>
            </div>
          </form>
        )}

        {modalMode === 'forgot' && (
          <div className="auth-form">
            <p className="forgot-contact-message">
              to reset your password, contact us at <strong>contact@wide-open.xyz</strong>
            </p>
            <div className="form-switch">
              <a href="#" onClick={switchToLogin}>back to login</a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AuthModal;